import { motion } from 'framer-motion'
import { useEffect } from 'react'
import { BlockRenderer } from '#app/components/block-renderer.tsx'
import { ViewTransitionLink } from '#app/components/view-transition-link.tsx'
import { prisma } from '#app/utils/db.server.ts'
import { buildMeta } from '#app/utils/meta.ts'
import { useNavigationTheme } from '#app/utils/navigation-theme-context.tsx'
import { type Route } from './+types/_marketing.diensten.index.ts'

export const meta: Route.MetaFunction = ({ data }) => {
	const page = data?.page
	return buildMeta({
		title: page?.metaTitle || 'Diensten',
		description:
			page?.metaDescription ||
			'Ontdek onze diensten: merkstrategie, merkidentiteit, campagne en meer. Ignite Identity helpt jouw merk groeien.',
		url: '/diensten',
	})
}

export async function loader() {
	// Look for an editable Page with slug 'diensten'
	const page = await prisma.page.findUnique({
		where: { slug: 'diensten', published: true },
		include: {
			blocks: {
				orderBy: { order: 'asc' },
			},
		},
	})

	// Fetch services and case studies for dynamic blocks
	const [services, caseStudies] = await Promise.all([
		prisma.service.findMany({
			include: {
				_count: {
					select: { caseStudies: true },
				},
			},
			orderBy: { name: 'asc' },
		}),
		prisma.caseStudy.findMany({
			where: { published: true },
			select: {
				id: true,
				title: true,
				slug: true,
				client: true,
				imageUrl: true,
				imageAlt: true,
				featured: true,
				order: true,
				services: {
					select: { id: true, name: true, slug: true },
				},
			},
			orderBy: [{ order: 'asc' }, { id: 'asc' }],
		}),
	])

	return {
		page,
		services,
		caseStudies,
		ctaConfig: page
			? {
					mode: page.ctaMode,
					title: page.ctaTitle,
					buttonText: page.ctaButtonText,
					buttonLink: page.ctaButtonLink,
					imageUrl: page.ctaImageUrl,
					imageAlt: page.ctaImageAlt,
					highlightWords: page.ctaHighlightWords,
					fontSizePreset: page.ctaFontSizePreset,
					showButton: page.ctaShowButton,
				}
			: null,
	}
}

// Animation variants (used for fallback layout)
const fadeIn = {
	initial: { opacity: 0, y: 12 },
	animate: { opacity: 1, y: 0 },
	transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] },
}

const staggerChildren = {
	animate: {
		transition: {
			staggerChildren: 0.12,
		},
	},
}

export default function DienstenIndex({ loaderData }: Route.ComponentProps) {
	const { page, services, caseStudies } = loaderData

	// If a dynamic page with blocks exists, render via BlockRenderer
	if (page && page.blocks && page.blocks.length > 0) {
		return (
			<main>
				<BlockRenderer
					blocks={page.blocks}
					services={services}
					caseStudies={caseStudies}
				/>
			</main>
		)
	}

	// Fallback: hardcoded layout when no dynamic page exists
	return <DienstenFallback services={services} />
}

function DienstenFallback({
	services,
}: {
	services: Array<{
		id: string
		name: string
		slug: string
		description: string | null
		_count: { caseStudies: number }
	}>
}) {
	const { setNavigationTheme } = useNavigationTheme()

	// Set navigation theme for dark hero
	useEffect(() => {
		setNavigationTheme(true, '#1a1a1a')
		return () => {
			setNavigationTheme(false, 'transparent')
		}
	}, [setNavigationTheme])

	return (
		<motion.main className="bg-white" initial="initial" animate="animate">
			{/* Hero Section */}
			<section className="relative bg-[#1a1a1a] pt-32 pb-16 md:pb-24">
				{/* Breadcrumb Navigation */}
				<motion.nav
					className="relative z-10 mx-auto max-w-7xl px-6 py-4 md:px-12 lg:px-24"
					variants={fadeIn}
				>
					<div className="flex items-center gap-2 text-sm text-white/60">
						<ViewTransitionLink
							to="/"
							className="transition-colors hover:text-white"
						>
							Home
						</ViewTransitionLink>
						<span>/</span>
						<span className="text-white">Diensten</span>
					</div>
				</motion.nav>

				<div className="relative z-10 mx-auto max-w-7xl px-6 md:px-12 lg:px-24">
					<motion.h1
						className="mb-6 text-4xl font-bold text-white md:text-6xl lg:text-7xl"
						variants={fadeIn}
					>
						Onze diensten
					</motion.h1>
					<motion.p
						className="max-w-2xl text-lg text-white/70 md:text-xl"
						variants={fadeIn}
					>
						Van strategie tot uitvoering - wij helpen merken groeien met
						doordachte oplossingen.
					</motion.p>
				</div>
			</section>

			{/* Services Grid */}
			<section className="px-6 py-16 md:px-12 md:py-24 lg:px-24">
				<div className="mx-auto max-w-7xl">
					{services.length === 0 ? (
						<motion.div className="py-16 text-center" variants={fadeIn}>
							<p className="text-lg text-gray-600">
								Er zijn nog geen diensten gepubliceerd.
							</p>
						</motion.div>
					) : (
						<motion.div
							className="grid gap-8 md:grid-cols-2 lg:grid-cols-3"
							variants={staggerChildren}
						>
							{services.map((service) => (
								<motion.article
									key={service.id}
									className="group"
									variants={fadeIn}
									whileHover={{ y: -4 }}
									transition={{ duration: 0.2 }}
								>
									<ViewTransitionLink
										to={`/diensten/${service.slug}`}
										className="block h-full rounded-xl border border-gray-200 p-6 transition-all hover:border-primary/30 hover:shadow-lg"
									>
										<h2 className="mb-3 text-xl font-bold text-gray-900 transition-colors group-hover:text-primary">
											{service.name}
										</h2>
										{service.description && (
											<p className="mb-4 line-clamp-3 text-gray-600">
												{service.description}
											</p>
										)}
										{service._count.caseStudies > 0 && (
											<p className="text-muted-foreground text-sm">
												{service._count.caseStudies} case
												{service._count.caseStudies !== 1 ? 's' : ''}
											</p>
										)}
									</ViewTransitionLink>
								</motion.article>
							))}
						</motion.div>
					)}
				</div>
			</section>
		</motion.main>
	)
}
